import { PROJECT_BASE_INFORMATION_INPUT_FIELDS } from './projectBaseInformationInput';
import { PROJECT_CALCULATION_INTERNAL_INPUT_FIELDS } from './projectCalculationInternalInput';
import { PROJECT_CUSTOMER_ADDITIONAL_INFORMATION_TOP_LEVEL_FIELDS } from './projectCustomerAdditionalInformationInput';
import { PROJECT_CUSTOMER_INSTALLATION_ADDRESS_TOP_LEVEL_FIELDS } from './projectCustomerInstallationAddressInput';
import { PROJECT_EMPLOYER_COMMUNICATION_INPUT_FIELDS } from './projectEmployerCommunicationInput';
import { PROJECT_INVOICE_APPROVAL_INPUT_FIELDS } from './projectInvoiceApprovalInput';
import { PROJECT_PARTIES_INPUT_FIELDS } from './projectPartiesInput';
import { PROJECT_POWER_COMPANY_INPUT_FIELDS } from './projectPowerCompanyInput';
import { PROJECT_POWER_COMPANY_COMMUNICATION_INPUT_FIELDS } from './projectPowerCompanyCommunicationInput';
import { PROJECT_MEASUREMENT_POINT_OPERATOR_INPUT_FIELDS } from './projectMeasurementPointOperatorInput';

/** Verification states of the tab Aufmaßprotokoll; the overall state is deprecated and read-only. */
const MEASUREMENT_VERIFICATION_STATE_FIELDS = [
  'measurementVerificationState',
  'measurementAcVerificationState',
  'measurementDcVerificationState',
  'measurementApprovementVerificationState',
];

/** Verification states of the tab Abnahmeprotokoll. */
const ACCEPTANCE_VERIFICATION_STATE_FIELDS = [
  'substructureVerificationState',
  'dcVerificationState',
  'acVerificationState',
  'scaffoldingVerificationState',
  'acceptanceApprovementVerificationState',
];

/** Verification states of the tab Service. */
const SERVICE_VERIFICATION_STATE_FIELDS = [
  'serviceVerificationState',
  'serviceApprovementVerificationState',
];

/**
 * Top-level project fields saved by blocks with their own mutation (ADR 0006 D2/D9). The page
 * save of the project detail leaves them out and keeps their stored values. Only whole
 * top-level fields: `updateProject` writes each sent top-level field whole. `customer` is saved
 * field by field by the five customer blocks and `customer.number` by project-base-information
 * (plan docs/work/_done/2026-10-project-block-customer-additional-information). `sales` stays in the
 * page save although the block project-base-information saves one of its fields
 * (plan docs/work/_done/2026-10-project-block-base-information, decided 12). `deferredState` is
 * saved whole by the block project-clarification. `investmentDeductionAllowance` is saved by the
 * three blocks project-plant-data, project-financing and project-iab-tax-data
 * (plan docs/work/_done/2026-10-project-block-iab-tax-data). The four party lists are saved whole by the
 * block project-parties (plan docs/work/_done/2026-10-project-block-parties). The three fields of
 * the section "Kommunikation mit Auftraggeber" are saved by the block
 * project-employer-communication (see feature page projekt-kommunikation). `technicalReview` is
 * saved by the three blocks project-technical-review-dc, project-technical-review-ac and
 * project-appointment-notes; its other fields have no UI (see feature pages
 * projekt-technische-pruefung and projekt-montage). `installation` and
 * `electrician` are saved by the block project-rough-planning; the block sets only two
 * `installation` fields, the others have no editor and stay stored (see feature page
 * projekt-montage). The fields of the tab EVU are saved by the blocks project-power-company,
 * project-power-company-communication and project-measurement-point-operator (see feature page
 * projekt-evu). `bank` is saved by the four form blocks of the tab Bank; its attachments are not in
 * the page payload (see feature page projekt-bank). The note of the section "Rechnungen: Freigabe"
 * is saved by the block project-invoice-approval; its state is not in the page payload (see
 * feature page projekt-angebote-rechnungen). `measurement` is saved per card by the block
 * project-measurement-report-data and its note by project-measurement-report-approval; the four
 * measurement verification states change only through their own actions, so a page loaded before a
 * state change cannot write the old state back (see feature page projekt-protokolle). The same
 * holds for the tab Abnahmeprotokoll: `enpalReport` is saved per card by the block
 * project-acceptance-report-data (its signatures through saveReportSignature), the top-level note
 * `acceptanceApprovementNote` by project-acceptance-report-approval, and the five acceptance
 * states through their own actions. Likewise the tab Service: `serviceReport` per card by
 * project-service-report-data, the top-level note `serviceApprovementNote` by
 * project-service-report-approval, and the two service states through their own actions. The
 * tab Qualitätsprotokoll saves `qualityReport` per card by project-quality-report-data, the tab
 * Sales Protokoll `salesReport` per card by project-sales-report-data.
 */
export const PROJECT_FIELDS_SAVED_BY_BLOCKS = [
  'solarPlant',
  'customer',
  'deferredState',
  'investmentDeductionAllowance',
  'technicalReview',
  'installation',
  'electrician',
  'bank',
  'measurement',
  ...MEASUREMENT_VERIFICATION_STATE_FIELDS,
  'enpalReport',
  'acceptanceApprovementNote',
  ...ACCEPTANCE_VERIFICATION_STATE_FIELDS,
  'serviceReport',
  'serviceApprovementNote',
  ...SERVICE_VERIFICATION_STATE_FIELDS,
  'qualityReport',
  'salesReport',
  ...PROJECT_BASE_INFORMATION_INPUT_FIELDS,
  ...PROJECT_CALCULATION_INTERNAL_INPUT_FIELDS,
  ...PROJECT_PARTIES_INPUT_FIELDS,
  ...PROJECT_CUSTOMER_INSTALLATION_ADDRESS_TOP_LEVEL_FIELDS,
  ...PROJECT_CUSTOMER_ADDITIONAL_INFORMATION_TOP_LEVEL_FIELDS,
  ...PROJECT_EMPLOYER_COMMUNICATION_INPUT_FIELDS,
  ...PROJECT_POWER_COMPANY_INPUT_FIELDS,
  ...PROJECT_POWER_COMPANY_COMMUNICATION_INPUT_FIELDS,
  ...PROJECT_MEASUREMENT_POINT_OPERATOR_INPUT_FIELDS,
  ...PROJECT_INVOICE_APPROVAL_INPUT_FIELDS,
];
